import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import './index.css';
import './i18n';

import App from './App';
import i18n from './i18n';

import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import { makeTheme } from './theme';
import { makeEmotionCache } from './muiRtlCache';
import { ColorModeContext } from './colorMode';

const THEME_KEY = 'alamen.theme.mode';

function readInitialMode() {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  const prefersDark =
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

function Root() {
  const [lang, setLang] = useState(i18n.language || 'en');
  const [mode, setMode] = useState(readInitialMode);

  const direction = lang === 'ar' ? 'rtl' : 'ltr';

  const cache = useMemo(() => makeEmotionCache(direction), [direction]);
  const theme = useMemo(() => makeTheme({ direction, mode }), [direction, mode]);

  useEffect(() => {
    const onChange = (lng) => setLang(lng);
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = lang || 'en';
    document.documentElement.dataset.theme = mode;
  }, [direction, lang, mode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_KEY, mode);
    } catch (_) {
      /* ignore */
    }
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () => setMode((m) => (m === 'light' ? 'dark' : 'light')),
      setMode
    }),
    [mode]
  );

  return (
    <React.StrictMode>
      <HelmetProvider>
        <ColorModeContext.Provider value={colorMode}>
          <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemeProvider>
          </CacheProvider>
        </ColorModeContext.Provider>
      </HelmetProvider>
    </React.StrictMode>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<Root />);
